import { Controller } from "@hotwired/stimulus"

// F10 link magnet — estimates buying-intent DMs missed per month.
// missed = engagement * intent_share * miss_rate
//
// Deep-linkable + shareable: ?engagement=&speed=&intent= prefill the form on
// load (speed/intent are validated against the select's own option values so
// a stale/garbage query string can't force an invalid state), and the URL is
// kept in sync (via replaceState) as the user answers so the current result
// can be copied/shared.
export default class extends Controller {
  static targets = ["engagement", "speed", "intent", "result", "copyLink"]

  DEFAULT_ENGAGEMENT = 800
  DEFAULT_SPEED = "0.20"
  DEFAULT_INTENT = "0.10"

  connect() {
    this.prefillFromUrl()
    this.recompute()
  }

  prefillFromUrl() {
    const params = new URLSearchParams(window.location.search)

    const engagementRaw = params.get("engagement")
    if (engagementRaw !== null) {
      const engagement = parseInt(engagementRaw, 10)
      if (Number.isInteger(engagement) && engagement >= 0 && String(engagement) === engagementRaw.trim()) {
        this.engagementTarget.value = engagement
      }
    }

    this.applySelectParam(this.speedTarget, params.get("speed"))
    this.applySelectParam(this.intentTarget, params.get("intent"))
  }

  applySelectParam(select, value) {
    if (value === null) return
    const validValues = Array.from(select.options).map((option) => option.value)
    if (validValues.includes(value)) {
      select.value = value
    }
  }

  recompute() {
    const engagement = Math.max(0, parseInt(this.engagementTarget.value || "0", 10))
    const missRate = parseFloat(this.speedTarget.value)
    const intentShare = parseFloat(this.intentTarget.value)
    const missed = Math.round(engagement * intentShare * missRate)
    this.resultTarget.textContent = missed.toLocaleString()

    this.syncUrl()
  }

  currentParams() {
    return {
      engagement: Math.max(0, parseInt(this.engagementTarget.value || "0", 10)),
      speed: this.speedTarget.value,
      intent: this.intentTarget.value,
    }
  }

  buildUrl() {
    const url = new URL(window.location.href)
    const { engagement, speed, intent } = this.currentParams()

    if (engagement !== this.DEFAULT_ENGAGEMENT) {
      url.searchParams.set("engagement", engagement)
    } else {
      url.searchParams.delete("engagement")
    }

    if (speed !== this.DEFAULT_SPEED) {
      url.searchParams.set("speed", speed)
    } else {
      url.searchParams.delete("speed")
    }

    if (intent !== this.DEFAULT_INTENT) {
      url.searchParams.set("intent", intent)
    } else {
      url.searchParams.delete("intent")
    }

    return url
  }

  syncUrl() {
    window.history.replaceState(window.history.state, "", this.buildUrl())
  }

  shareUrl() {
    return this.buildUrl().toString()
  }

  async copyLink() {
    const url = this.shareUrl()

    try {
      await navigator.clipboard.writeText(url)
    } catch (_err) {
      this.fallbackCopy(url)
    }

    this.flashCopied()
  }

  fallbackCopy(text) {
    const textarea = document.createElement("textarea")
    textarea.value = text
    textarea.style.position = "fixed"
    textarea.style.opacity = "0"
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    try {
      document.execCommand("copy")
    } finally {
      document.body.removeChild(textarea)
    }
  }

  flashCopied() {
    if (!this.hasCopyLinkTarget) return

    if (this.copyResetTimeout) clearTimeout(this.copyResetTimeout)

    if (this.originalCopyLabel === undefined) {
      this.originalCopyLabel = this.copyLinkTarget.textContent
    }

    this.copyLinkTarget.textContent = "Link copied ✓"
    this.copyResetTimeout = setTimeout(() => {
      this.copyLinkTarget.textContent = this.originalCopyLabel
    }, 2000)
  }
}
